import { Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import ChipLabel, { type CHIP_COLORS } from '/@/components/chip-label';
import { type UserNode, useAncestors, useOwnUserNode } from '/@/hooks/nodes';
import { getPluralUserTypes, getUserTypes } from '/@/utils';

const ICON_SX = { color: 'primary.mui.inactive', width: '24px' } as const;

const AncestorsBarV2 = ({
    contactItems,
    ancestorsFromNodeId,
    additionalNodes,
    children,
}: {
    contactItems?: { key: string; Icon?: React.ElementType; value: string }[];
    ancestorsFromNodeId?: number;
    additionalNodes?: UserNode[];
    children?: React.ReactNode;
}) => {
    const userTypes = getUserTypes();
    const pluralUserTypes = getPluralUserTypes();
    const ownUserNodeQuery = useOwnUserNode();

    const ancestorsQuery = useAncestors(
        {
            node: ancestorsFromNodeId,
            types: ['consultant', 'rv', 'advisor'],
        },
        {
            enabled: !!ancestorsFromNodeId,
        }
    );
    const ancestors = useMemo(() => {
        const allNodes = [...(ancestorsQuery.data || []), ...(additionalNodes || [])];
        return allNodes;
    }, [ancestorsQuery.data, additionalNodes]);

    const order = ['squad_coach', 'consultant', 'rv', 'advisor', 'grower', 'other'];
    const orderedAncestors = useMemo(() => {
        const filtered =
            ancestors
                ?.filter((ancestor) => userTypes[ancestor?.type] && ancestor.id !== ownUserNodeQuery.data?.id)
                .toSorted((a, b) => order.indexOf(a.type) - order.indexOf(b.type)) ?? [];
        const byType = filtered.reduce<Record<string, UserNode[]>>((acc, ancestor) => {
            const t = ancestor.type;
            if (!acc[t]) acc[t] = [];
            acc[t].push(ancestor);
            return acc;
        }, {});
        return order
            .filter((type) => byType[type]?.length)
            .map((type) => {
                const group = byType[type];
                if (!group?.length) return null;

                return (
                    <Stack direction='row' spacing={1} key={type} alignItems='center'>
                        {group.map((ancestor) => (
                            <ChipLabel
                                key={ancestor.id}
                                label={ancestor.name}
                                type={ancestor.type as keyof typeof CHIP_COLORS}
                            />
                        ))}
                    </Stack>
                );
            });
    }, [ancestors, userTypes, pluralUserTypes, ownUserNodeQuery.data?.id]);

    if (!contactItems && orderedAncestors.length === 0 && !children) return null;

    return (
        <Stack
            direction='column'
            alignItems='flex-start'
            justifyContent='flex-start'
            spacing={0.5}
            mt={2}
            flexWrap='wrap'
            useFlexGap
        >
            {contactItems && (
                <Stack className='mp-mask' direction='row' spacing={2} alignItems='center' flexWrap='wrap' useFlexGap>
                    {contactItems.map(({ key, Icon: ContactIcon, value }) => (
                        <Stack key={key} direction='row' spacing={1} alignItems='center'>
                            {ContactIcon && <ContactIcon sx={ICON_SX} />}
                            <Typography color='primary.mui.inactive' variant='body1'>
                                {value}
                            </Typography>
                        </Stack>
                    ))}
                </Stack>
            )}
            <Stack className='mp-mask' direction='row' spacing={2} alignItems='center' flexWrap='wrap' useFlexGap>
                {orderedAncestors}
            </Stack>
            {children}
        </Stack>
    );
};

export default AncestorsBarV2;
